import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mic, Smartphone, Zap, Trophy, StarIcon, MessageSquare, Users, Volume2 } from 'lucide-react';
import { Badge, SectionHeading, PhoneMockup, TestimonialCard } from '../components/ui';
import { CharacterCard, Character } from '../components/CharacterCard';
import { PricingCard, PricingPlan } from '../components/PricingCard';
import { HilariousMeter } from '../components/HilariousMeter';
const CHARACTERS: Character[] = [{
  id: 'vinny',
  name: 'Vinny Capuano',
  hometown: 'Brooklyn, NY',
  color: 'bg-blue-900',
  borderColorClass: 'border-blue-500',
  tagline: 'Settles every argument with a deli meat analogy.',
  quote: "Whaddya think you're doin' startin' Deebo on a BYE week? You absolute stunad. My grandmother could run a better route, and she's been dead since '04."
}, {
  id: 'dale',
  name: 'Dale & Earl',
  hometown: 'Harlan County, KY',
  color: 'bg-green-900',
  borderColorClass: 'border-green-600',
  tagline: 'Two cousins. One microphone. Zero indoor voices.',
  quote: "Well I'll be dadgum... you started WHO? Boy, even the mule woulda known better than to trust a Jets quarterback on a Thursday."
}, {
  id: 'marisol',
  name: 'Marisol Cruz',
  hometown: 'South Bronx, NY',
  color: 'bg-rose-900',
  borderColorClass: 'border-rose-500',
  tagline: 'Every emotion at 100%. Every play is life or death.',
  quote: 'Ay, mira — Josh Allen casi me da un ataque al corazón. CASI, David! I am lighting a candle for your absolutely pathetic roster.'
}, {
  id: 'giuseppe',
  name: 'Giuseppe Conti',
  hometown: 'Palermo, Sicily',
  color: 'bg-red-950',
  borderColorClass: 'border-red-800',
  tagline: 'Silence is his weapon. Disappointment is his art.',
  quote: '...You benched Jefferson. (long, agonizing pause) ...I have nothing more to say to you. You are dead to me.'
}, {
  id: 'daniel',
  name: 'Daniel Ashworth',
  hometown: 'London, UK',
  color: 'bg-slate-800',
  borderColorClass: 'border-slate-400',
  tagline: 'Forty years of rugby did not prepare him for this.',
  quote: "I've watched 40 years of rugby. I've seen men lose teeth and keep running. But nothing prepared me for the absolute cowardice of your flex spot."
}, {
  id: 'gary',
  name: "G'Day Gary",
  hometown: 'Melbourne, AU',
  color: 'bg-orange-900',
  borderColorClass: 'border-orange-500',
  tagline: 'Volume permanently set to 11. Pads are for cowards.',
  quote: 'STREWTH mate, that was more painful than taking a box jellyfish straight to the face! Absolute RIPPER of a choke job!'
}, {
  id: 'kevin',
  name: 'Kevin Chen',
  hometown: 'Flushing, Queens, NY',
  color: 'bg-red-900',
  borderColorClass: 'border-red-600',
  tagline: 'He came for dumplings. He stayed for the fantasy roast.',
  quote: 'YOU START DANIEL JONES?! My grandmother in Flushing make better decision with her eyes closed! DANIEL JONES! I drop my dumpling watching this!'
}, {
  id: 'marcus',
  name: 'Marcus — The Flamboyant',
  hometown: 'West Hollywood, CA',
  color: 'bg-fuchsia-900',
  borderColorClass: 'border-fuchsia-500',
  tagline: 'The audacity. The sheer unmitigated audacity.',
  quote: 'Honey... you started WHO at flex? The disrespect to my intelligence. The absolute GALL. I need a moment.'
}, {
  id: 'ricky',
  name: 'Ricky Rotardo',
  hometown: 'Every Last Place Finish',
  color: 'bg-yellow-900',
  borderColorClass: 'border-yellow-400',
  tagline: 'The name no one wants to be compared to.',
  quote: "Ricky Rotardo isn't a voice. He's a warning. When Vinny says 'you're playing like Ricky Rotardo,' you know you've hit rock bottom."
}];
const PRICING: PricingPlan[] = [{
  id: 'draft',
  title: 'Draft Day Rental',
  price: '$19.99',
  period: '',
  badge: 'TRY IT TONIGHT',
  badgeColor: 'lime',
  borderClass: 'border-lime',
  glowClass: 'hover:shadow-[0_0_30px_rgba(200,255,0,0.2)]',
  description: 'One-night access. Perfect for draft night. Zero commitment. Like your last relationship.',
  ctaText: 'RENT FOR TONIGHT',
  ctaClass: 'bg-transparent border-2 border-lime text-lime hover:bg-lime hover:text-charcoal'
}, {
  id: 'starter',
  title: 'Starter',
  price: '$49',
  period: 'season',
  borderClass: 'border-white/20',
  glowClass: 'hover:border-white/50',
  description: '1 league. 2 commentators. Tiers 1-2. For people who are afraid of their own group chat.',
  ctaText: 'START YOUR SEASON',
  ctaClass: 'bg-white text-charcoal hover:bg-slate-200'
}, {
  id: 'playoff',
  title: 'Playoff Push',
  price: '$99',
  period: 'season',
  badge: '⭐ MOST POPULAR',
  badgeColor: 'orange',
  borderClass: 'border-orange',
  glowClass: 'shadow-[0_0_20px_rgba(255,107,0,0.15)] hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] scale-105 z-10',
  description: '1 league. Full character cast. Tiers 1-3 unlocked. The sweet spot of emotional damage.',
  ctaText: 'GO PLAYOFF PUSH',
  ctaClass: 'bg-orange text-white hover:bg-orange/80',
  popular: true
}, {
  id: 'afterdark',
  title: 'After Dark',
  price: '$149',
  period: 'season',
  badge: '🔥 ADULTS ONLY',
  badgeColor: 'crimson',
  borderClass: 'border-crimson',
  glowClass: 'hover:shadow-[0_0_30px_rgba(220,38,38,0.2)]',
  description: '1 league. Full cast. All 4 tiers. Clone upsell included. HR will definitely want to see you.',
  ctaText: 'UNLOCK AFTER DARK',
  ctaClass: 'bg-crimson text-white hover:bg-crimson/80'
}, {
  id: 'commissioner',
  title: 'Commissioner Pack',
  price: '$199',
  period: 'season',
  badge: '👑 BEST VALUE',
  badgeColor: 'gold',
  borderClass: 'border-gold',
  glowClass: 'hover:shadow-[0_0_30px_rgba(245,197,24,0.2)]',
  description: '3 leagues. Every feature. Every tier. Commissioner goes FREE when 8+ members pay.',
  subNote: 'Loser Pays mechanic available — collect upfront, auto-charge the idiot in last place at season end.',
  ctaText: 'GET COMMISSIONER PACK',
  ctaClass: 'bg-gold text-charcoal hover:bg-yellow-400'
}];
const PRICING_TIERS: PricingPlan[] = [{
  id: 'schoolyard',
  title: 'The Schoolyard',
  price: '$4.99',
  period: 'person/season',
  borderClass: 'border-blue-500',
  glowClass: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.2)]',
  description: 'Clean comedy. No swearing. Family friendly roasts.',
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-blue-500 text-white hover:bg-blue-600',
  fundraisingNote: true
}, {
  id: 'lockerroom',
  title: 'The Locker Room',
  price: '$9.99',
  period: 'person/season',
  badge: '⭐ MOST POPULAR',
  badgeColor: 'orange',
  borderClass: 'border-orange',
  glowClass: 'shadow-[0_0_20px_rgba(255,107,0,0.15)] hover:shadow-[0_0_40px_rgba(255,107,0,0.3)] scale-105 z-10',
  description: 'Real talk. Unfiltered commentary. The full roast experience.',
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-orange text-white hover:bg-orange/80',
  popular: true,
  fundraisingNote: true
}, {
  id: 'goodoldboys',
  title: 'Good Old Boys',
  price: '$14.99',
  period: 'person/season',
  borderClass: 'border-green-600',
  glowClass: 'hover:shadow-[0_0_20px_rgba(22,163,74,0.2)]',
  description: 'Premium roasts. Smack talk intel. Owner-submitted dirt used in commentary.',
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-green-600 text-white hover:bg-green-700',
  fundraisingNote: true
}, {
  id: 'theroast',
  title: 'The Roast',
  price: '$19.99',
  period: 'person/season',
  badge: '🔥 NO MERCY',
  badgeColor: 'crimson',
  borderClass: 'border-crimson',
  glowClass: 'shadow-[0_0_30px_rgba(220,38,38,0.3)] hover:shadow-[0_0_50px_rgba(220,38,38,0.5)]',
  description: 'No limits. No mercy. Unlimited clips. Full character rotation.',
  ctaText: 'Pick This Tier',
  ctaClass: 'bg-crimson text-white hover:bg-red-700',
  fundraisingNote: true
}];
const TICKER_ITEMS = ['🏈 Hinge Hunters — VINNY: You started Deebo on a BYE week? You absolute stunad.', '🔥 Taco Tuesday — MARISOL: ¡Ay Dios mío! Thirty points down and it is only halftime! I am lighting a candle.', '💀 Fantasy Funeral — GIUSEPPE: ...I have nothing to say. (long pause) Nothing. You disgust me.', "🎤 Kevin's Dumplings — KEVIN: YOU BENCH JOSH ALLEN?! Even my dumpling cart has better judgment!", '💅 Slay All Day — MARCUS: The audacity of that flex pick. I am physically SHAKEN.', '🤠 Mudflap Mafia — DALE: That boy could not catch a cold in a snowstorm wearing a wet t-shirt.'];
export const HomePage = () => {
  return <div className="w-full" data-id="element-576">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-stadium-grain bg-cover bg-center" data-id="element-577">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/80 to-charcoal" data-id="element-578"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-579">
          <div className="max-w-5xl mx-auto text-center" data-id="element-580">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-581">
              <div className="inline-flex items-center gap-2 bg-dark/80 backdrop-blur border border-white/10 rounded-full px-4 py-2 mb-8" data-id="element-582">
                <Trophy size={16} className="text-gold" data-id="element-583" />
                <span className="text-sm font-bold uppercase tracking-wider text-gold" data-id="element-584">
                  Commissioner Pack — 3 Leagues Included
                </span>
              </div>

              <h1 className="text-7xl md:text-[140px] leading-[0.85] font-black font-heading uppercase -skew-x-6 mb-8 text-shadow-broadcast" data-id="element-585">
                <span className="block text-white" data-id="element-586">YOUR LEAGUE.</span>
                <span className="block text-lime" data-id="element-587">OUR ROAST.</span>
                <span className="block text-white" data-id="element-588">ALL SEASON.</span>
              </h1>

              <p className="text-xl md:text-3xl text-slate font-medium max-w-3xl mx-auto mb-12 leading-tight" data-id="element-589">
                AI commentary so savage your league group chat will need a
                therapist. Audio clips + texts delivered after every game.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12" data-id="element-590">
                <Link to="/pricing" className="w-full sm:w-auto bg-lime text-charcoal px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-white hover:scale-105 transition-all shadow-[0_0_30px_rgba(200,255,0,0.4)]" data-id="element-591">
                  Ruin Friendships — $19.99
                </Link>
                <button className="w-full sm:w-auto bg-transparent border-2 border-white text-white px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-white hover:text-charcoal transition-all" data-id="element-592">
                  Hear The Carnage
                </button>
              </div>

              <div className="flex items-center justify-center gap-2 text-slate" data-id="element-593">
                <div className="flex text-gold" data-id="element-594">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} size={16} className="fill-current" data-id="element-595" />)}
                </div>
                <span className="text-sm font-bold" data-id="element-596">
                  "My league threatened to physically fight me. 10/10." —
                  Commissioner, 12-team PPR
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LIVE TICKER */}
      <div className="bg-dark border-t border-lime py-3 overflow-hidden relative flex items-center" data-id="element-597">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-dark to-transparent z-10 flex items-center px-4" data-id="element-598">
          <span className="text-xs font-bold uppercase tracking-wider text-lime bg-dark px-2 py-1 rounded border border-lime/30 shadow-[0_0_10px_rgba(200,255,0,0.2)] whitespace-nowrap" data-id="element-599">
            LIVE FROM THE LEAGUES
          </span>
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-dark to-transparent z-10" data-id="element-600"></div>

        <div className="flex whitespace-nowrap animate-ticker pl-32" data-id="element-601">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => <span key={i} className="text-white font-mono text-sm mx-8 flex items-center gap-2" data-id="element-602">
              <span className="w-2 h-2 rounded-full bg-lime animate-pulse" data-id="element-603"></span>
              {item}
            </span>)}
        </div>
      </div>

      {/* HILARIOUS METER SECTION */}
      <section className="py-32 bg-charcoal relative border-t border-white/5" data-id="element-604">
        <div className="container mx-auto px-4" data-id="element-605">
          <SectionHeading title="PICK YOUR LEVEL. PICK YOUR POISON." subtitle="From 'Aww shucks' to 'I am calling my lawyer.'" className="text-center" data-id="element-606" />
          <HilariousMeter data-id="element-607" />
        </div>
      </section>

      {/* CHARACTER SHOWCASE */}
      <section className="py-32 bg-dark relative overflow-hidden border-y border-white/5" data-id="element-608">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-lime/50 to-transparent" data-id="element-609"></div>

        <div className="container mx-auto px-4" data-id="element-610">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6" data-id="element-611">
            <SectionHeading title="MEET THE CAST" subtitle="11 accents. Zero mercy. Pick your executioners." className="mb-0" data-id="element-612" />
            <Link to="/characters" className="inline-flex items-center gap-2 text-lime font-bold uppercase tracking-wider hover:text-white transition-colors" data-id="element-613">
              See The Full Cast <Zap size={16} className="fill-current" data-id="element-614" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-615">
            {CHARACTERS.map(char => <CharacterCard key={char.id} character={char} data-id="element-616" />)}
          </div>

          <div className="mt-16 text-center md:hidden" data-id="element-617">
            <Link to="/characters" className="bg-white text-charcoal px-8 py-4 rounded-xl font-black font-heading text-xl uppercase tracking-wide inline-block" data-id="element-618">
              See The Full Cast
            </Link>
          </div>
        </div>
      </section>

      {/* OWNER SMACK TALK INTEL */}
      <section className="py-32 bg-charcoal relative overflow-hidden" data-id="element-619">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-orange/5 blur-[150px] rounded-full pointer-events-none" data-id="element-620"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-621">
          <SectionHeading title="YOUR LEAGUE KNOWS THINGS. NOW SO DO WE." subtitle="Because generic trash talk is for cowards. Weaponize their real lives." className="text-center mb-20" data-id="element-622" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-623">
            <div className="space-y-12" data-id="element-624">
              <div className="flex gap-6" data-id="element-625">
                <div className="w-16 h-16 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0 border border-orange/30" data-id="element-626">
                  <MessageSquare className="text-orange" size={32} data-id="element-627" />
                </div>
                <div data-id="element-628">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2 text-white" data-id="element-629">
                    1. SUBMIT THE DIRT
                  </h3>
                  <p className="text-slate text-lg" data-id="element-630">
                    Voice memo or text. Tell us the real stuff. He is 5-foot-7
                    but tells people he is 6-foot. He cried at his own birthday
                    party. He drives a lifted truck but can't parallel park.
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-631">
                <div className="w-16 h-16 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0 border border-orange/30" data-id="element-632">
                  <Zap className="text-orange" size={32} data-id="element-633" />
                </div>
                <div data-id="element-634">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2 text-white" data-id="element-635">
                    2. WE FEED THE AI
                  </h3>
                  <p className="text-slate text-lg" data-id="element-636">
                    Your intel goes directly into the commentary engine. The
                    characters now know things they shouldn't legally know.
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-637">
                <div className="w-16 h-16 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0 border border-orange/30" data-id="element-638">
                  <Volume2 className="text-orange" size={32} data-id="element-639" />
                </div>
                <div data-id="element-640">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2 text-white" data-id="element-641">
                    3. WATCH THEM SQUIRM
                  </h3>
                  <p className="text-slate text-lg" data-id="element-642">
                    Personalized roasts hit different when they reference that
                    time he threw up at Applebee's in 2018.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-id="element-643">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="bg-dark p-6 rounded-2xl border border-white/10 opacity-70" data-id="element-644">
                <div className="flex justify-between items-center mb-4" data-id="element-645">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate" data-id="element-646">
                    BEFORE (BORING)
                  </span>
                </div>
                <p className="text-lg text-slate italic" data-id="element-647">
                  "Hinge Hunters lost by 30 this week. Tough break, buddy."
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-dark p-8 rounded-2xl border-2 border-orange shadow-[0_0_30px_rgba(255,107,0,0.2)] relative" data-id="element-648">
                <div className="absolute -top-3 -right-3" data-id="element-649">
                  <span className="bg-orange text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-650">
                    WITH INTEL
                  </span>
                </div>
                <div className="flex justify-between items-center mb-4" data-id="element-651">
                  <span className="text-xs font-bold uppercase tracking-wider text-orange" data-id="element-652">
                    AFTER (DEVASTATING)
                  </span>
                </div>
                <p className="text-xl text-white font-medium italic leading-relaxed" data-id="element-653">
                  "Hinge Hunters lost by 30. I heard from your own league that
                  you were face-down sick the night before this matchup, and
                  your lineup looks exactly how you felt. Maybe lay off the
                  tequila on Saturday nights, David. You're 34."
                </p>
              </motion.div>

              <div className="pt-4 text-center" data-id="element-654">
                <button className="bg-orange text-white px-8 py-4 rounded-xl font-black font-heading text-xl uppercase tracking-wide hover:bg-orange/80 transition-all w-full sm:w-auto" data-id="element-655">
                  Weaponize Your Intel
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFFICIAL NOTIFICATION CALLS */}
      <section className="py-32 bg-dark border-y border-white/5 relative overflow-hidden" data-id="element-656">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-5" data-id="element-657"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-658">
          <SectionHeading title="SOME DECISIONS REQUIRE OFFICIAL ACTION." subtitle="These aren't roasts. These are deadpan, fake-official outbound phone calls triggered by catastrophic lineup decisions." className="text-center mb-16" data-id="element-659" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-660">
            {[{
            title: 'DRUG TEST NOTICE',
            icon: '💊',
            color: 'text-red-500',
            border: 'border-red-500/30',
            bg: 'bg-red-500/10',
            desc: 'Triggered when an owner starts a player who is on IR or a Bye week.'
          }, {
            title: 'HR COMPLAINT',
            icon: '📋',
            color: 'text-blue-400',
            border: 'border-blue-400/30',
            bg: 'bg-blue-400/10',
            desc: 'Triggered when an owner drops a top-20 player to the waiver wire.'
          }, {
            title: 'COMMISSIONER INVESTIGATION',
            icon: '🔍',
            color: 'text-gold',
            border: 'border-gold/30',
            bg: 'bg-gold/10',
            desc: 'Triggered by wildly lopsided trades that reek of collusion.'
          }, {
            title: 'WELLNESS CHECK',
            icon: '🚑',
            color: 'text-lime',
            border: 'border-lime/30',
            bg: 'bg-lime/10',
            desc: 'Triggered when an owner loses by 50+ points. We just want to make sure they are okay.'
          }].map((item, i) => <motion.div key={i} whileHover={{
            y: -5
          }} className={`p-6 rounded-2xl border ${item.border} ${item.bg} backdrop-blur-sm`} data-id="element-661">
                <div className="text-4xl mb-4" data-id="element-662">{item.icon}</div>
                <h4 className={`font-heading font-black text-xl uppercase tracking-wide mb-2 ${item.color}`} data-id="element-663">
                  {item.title}
                </h4>
                <p className="text-sm text-slate" data-id="element-664">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* COMMUNITY VOICE COMPETITION */}
      <section className="py-32 bg-dark relative overflow-hidden border-y border-white/5" data-id="element-665">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/10 via-dark to-dark" data-id="element-666"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-667">
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-668">
            <h2 className="text-5xl md:text-7xl font-black font-heading uppercase -skew-x-6 mb-6 text-gold" data-id="element-669">
              CREATE THE NEXT GREAT CHARACTER
            </h2>
            <p className="text-xl md:text-2xl text-slate" data-id="element-670">
              Think you're funny? Prove it. Design a voice. Win a spot on the
              platform. Earn royalties every time it ruins someone's Sunday.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16" data-id="element-671">
            {[{
            step: '1',
            title: 'DESIGN YOUR CHARACTER',
            desc: 'Name, personality, accent, and your most unhinged sample lines.'
          }, {
            step: '2',
            title: 'SUBMIT FOR REVIEW',
            desc: 'Our team evaluates every submission to make sure it is actually funny.'
          }, {
            step: '3',
            title: 'GET SELECTED',
            desc: 'Winners join the official cast permanently. Fame and glory await.'
          }, {
            step: '4',
            title: 'EARN ROYALTIES',
            desc: 'Every time your character roasts someone, you get paid real human money.'
          }].map((item, i) => <motion.div key={i} whileHover={{
            y: -5
          }} className="bg-charcoal p-6 rounded-2xl border border-white/10 text-center relative" data-id="element-672">
                <div className="w-12 h-12 bg-gold/20 text-gold rounded-full flex items-center justify-center font-black font-heading text-2xl mx-auto mb-4" data-id="element-673">
                  {item.step}
                </div>
                <h4 className="font-heading font-black text-xl uppercase tracking-wide mb-2 text-white" data-id="element-674">
                  {item.title}
                </h4>
                <p className="text-sm text-slate" data-id="element-675">{item.desc}</p>
              </motion.div>)}
          </div>

          <div className="max-w-3xl mx-auto bg-charcoal border-2 border-gold rounded-2xl p-8 text-center shadow-[0_0_40px_rgba(245,197,24,0.15)]" data-id="element-676">
            <p className="text-xl text-white font-medium mb-8" data-id="element-677">
              This is not a contest. This is a creator economy. Your character.
              Your royalties. Forever. Don't let your memes be dreams.
            </p>
            <Link to="/creator" className="inline-block bg-gold text-charcoal px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-white hover:scale-105 transition-all shadow-xl" data-id="element-678">
              Learn More & Apply
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-32 bg-charcoal" data-id="element-679">
        <div className="container mx-auto px-4" data-id="element-680">
          <SectionHeading title="HOW IT WORKS" subtitle="Four steps to completely alienating your friends." className="text-center mb-20" data-id="element-681" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-682">
            <div className="space-y-12" data-id="element-683">
              <div className="flex gap-6" data-id="element-684">
                <div className="w-16 h-16 rounded-2xl bg-lime/10 flex items-center justify-center shrink-0 border border-lime/30" data-id="element-685">
                  <Zap className="text-lime fill-lime" size={32} data-id="element-686" />
                </div>
                <div data-id="element-687">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-688">
                    1. Connect Your Sleeper League
                  </h3>
                  <p className="text-slate text-lg" data-id="element-689">
                    Link your league in 60 seconds. We pull your roster, scores,
                    and all your terrible draft decisions.
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-690">
                <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center shrink-0 border border-blue-500/30" data-id="element-691">
                  <Mic className="text-blue-500" size={32} data-id="element-692" />
                </div>
                <div data-id="element-693">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-694">
                    2. Pick Your Tier
                  </h3>
                  <p className="text-slate text-lg" data-id="element-695">
                    Set the Hilarious Meter from "family-friendly" to "I need to
                    call my lawyer."
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-696">
                <div className="w-16 h-16 rounded-2xl bg-orange/10 flex items-center justify-center shrink-0 border border-orange/30" data-id="element-697">
                  <Users className="text-orange" size={32} data-id="element-698" />
                </div>
                <div data-id="element-699">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-700">
                    3. Pick Your Characters
                  </h3>
                  <p className="text-slate text-lg" data-id="element-701">
                    Choose your commentary team from our growing cast of
                    unhinged voices.
                  </p>
                </div>
              </div>

              <div className="flex gap-6" data-id="element-702">
                <div className="w-16 h-16 rounded-2xl bg-crimson/10 flex items-center justify-center shrink-0 border border-crimson/30" data-id="element-703">
                  <Smartphone className="text-crimson" size={32} data-id="element-704" />
                </div>
                <div data-id="element-705">
                  <h3 className="font-heading font-black text-3xl uppercase tracking-wide mb-2" data-id="element-706">
                    4. Get Roasted Automatically
                  </h3>
                  <p className="text-slate text-lg" data-id="element-707">
                    Score changes ≥5 pts trigger roasts. Your league chat
                    explodes. You realize you are the Ricky Rotardo of your
                    league.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center" data-id="element-708">
              <PhoneMockup sender="VINNY CAPUANO" message="🏈 Hinge Hunters lost by 30. Josh Allen scored 23 points... for the OTHER team. Sleep well, you absolute clown." data-id="element-709" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-32 bg-dark border-y border-white/5" data-id="element-710">
        <div className="container mx-auto px-4" data-id="element-711">
          <SectionHeading title="WHAT COMMISSIONERS ARE SAYING" subtitle="(The ones who haven't been overthrown yet)" className="text-center mb-16" data-id="element-712" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-713">
            <TestimonialCard quote="My league threatened to quit if I don't renew next season. I think they hate me, but they love the app." author="Dave S." leagueType="10-team Half-PPR" data-id="element-714" />
            <TestimonialCard quote="We have a 'Ricky Rotardo of the Week' trophy now. It's a toilet seat painted gold. This is what fantasy was missing." author="Marcus T." leagueType="14-team Dynasty" data-id="element-715" />
            <TestimonialCard quote="School Spirit Mode for our PTA league was perfect. Even the kids are invested now. Timmy cried when his dad benched Mahomes." author="Sarah L." leagueType="8-team School League" data-id="element-716" />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-32 bg-charcoal relative" data-id="element-717">
        <div className="container mx-auto px-4" data-id="element-718">
          <SectionHeading title="SIMPLE PRICING. HILARIOUS RETURNS." subtitle="Pick your league's personality. Pay however you want. A portion of every subscription goes to school fundraising programs." className="text-center mb-20 max-w-4xl mx-auto flex flex-col items-center" data-id="element-719" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch max-w-[1400px] mx-auto" data-id="element-720">
            {PRICING_TIERS.map(plan => <PricingCard key={plan.id} plan={plan} data-id="element-721" />)}
          </div>

          <div className="mt-16 text-center" data-id="element-722">
            <Link to="/pricing" className="inline-block text-lime font-bold uppercase tracking-wider hover:text-white transition-colors text-xl" data-id="element-723">
              See payment options, comparison table, and more →
            </Link>
          </div>
        </div>
      </section>

      {/* COMMISSIONER BANNER */}
      <section className="py-20 bg-lime text-charcoal relative overflow-hidden" data-id="element-724">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" data-id="element-725"></div>
        <div className="container mx-auto px-4 relative z-10 text-center" data-id="element-726">
          <h2 className="text-6xl md:text-8xl font-black font-heading uppercase -skew-x-6 mb-6 tracking-tighter" data-id="element-727">
            COMMISSIONERS GO FREE
          </h2>
          <p className="text-2xl font-bold max-w-2xl mx-auto mb-10" data-id="element-728">
            Get 8 league members to pay and your season is on us. Share your
            link. Collect your glory. Let the peasants pay.
          </p>
          <button className="bg-charcoal text-lime px-10 py-5 rounded-xl font-black font-heading text-2xl uppercase tracking-wide hover:bg-dark hover:scale-105 transition-all shadow-2xl" data-id="element-729">
            Claim Your Crown
          </button>
        </div>
      </section>
    </div>;
};